import React, { useState } from "react";
import mixpanel from "mixpanel-browser";

import { DashboardAPI } from "utils/api";

import Dialog, { CloseDialog } from "components/Dialog";
import Input from "components/Input";
import Button from "components/Button";

import classes from "./styles.module.scss";

const TransferCreditsModal = ({
    credits,
    isOpen,
    closeModal,
    transferTo,
    transferToName,
    setAlert,
    transferFrom,
    organizationId,
    updateUser,
}) => {
    // empty, not 0: the input clears a zero on focus, and a guard that put it straight back left
    // the placeholder stuck in front of everything that was typed after it
    const [creditsToTransfer, setCreditsToTransfer] = useState(null);
    const [isTransferring, setIsTransferring] = useState(false);
    const [error, setError] = useState("");

    const amount = Number(creditsToTransfer);
    const isInvalidAmount = !creditsToTransfer || !Number.isInteger(amount) || amount <= 0 || amount > credits;

    const handleClose = () => {
        if (isTransferring) return;
        setCreditsToTransfer(null);
        setError("");
        closeModal();
    };

    const handleTransfer = async (e) => {
        e.preventDefault();
        if (isTransferring || isInvalidAmount) return;

        setIsTransferring(true);
        setError("");

        try {
            const response = await DashboardAPI({
                method: "POST",
                url: "/transferCredits",
                data: {
                    transferFrom,
                    transferTo,
                    amount,
                    organizationId,
                },
            });

            // DashboardAPI returns the axios error object instead of throwing
            if (!response?.data || response?.isAxiosError) {
                const message =
                    response?.response?.data?.message ||
                    response?.message ||
                    "An error occurred while transferring credits";
                throw new Error(message);
            }

            const serverCredits = response.data?.creditSummary?.credits;
            updateUser({
                creditSummary: {
                    credits: serverCredits != null ? serverCredits : credits - amount,
                },
            });

            mixpanel.track("Credits Transfer", {
                UserId: transferFrom,
                TransferTo: transferTo,
                TransferToName: transferToName,
                Credits: amount,
                OrganizationId: organizationId,
            });

            setAlert({
                open: true,
                message: `Successfully transferred ${amount} credits to ${transferToName}`,
                anchorOrigin: { vertical: "top", horizontal: "center" },
                direction: "down",
                severity: "success",
            });
            setCreditsToTransfer(null);
            closeModal();
        } catch (err) {
            // the dialog covers the page, so a snackbar behind it is never read
            setError(err?.message || "An error occurred while transferring credits");
        } finally {
            setIsTransferring(false);
        }
    };

    return (
        <Dialog open={isOpen}>
            <CloseDialog closeActions={handleClose} />
            <div className={classes.modalContainer}>
                <h2 className={classes.modalTitle}>Transfer Credits</h2>
                <div className={classes.modalBodyContainer}>
                    <p>
                        You have {credits} credits available. How many would you like to transfer to{" "}
                        <strong>{transferToName}</strong>?
                    </p>
                    <Input
                        type="number"
                        label="Credits to transfer"
                        placeholder="0"
                        setTouched
                        input={{
                            value: creditsToTransfer == null ? "" : creditsToTransfer,
                            onChange: (value) => {
                                setCreditsToTransfer(value);
                                setError("");
                            },
                        }}
                        meta={{
                            error:
                                error ||
                                (amount > credits
                                    ? "You don't have enough credits"
                                    : creditsToTransfer && !Number.isInteger(amount)
                                      ? "Enter a whole number of credits"
                                      : null),
                            invalid: Boolean(
                                error || amount > credits || (creditsToTransfer && !Number.isInteger(amount))
                            ),
                        }}
                    />
                </div>
                <div className={classes.buttonsContainer}>
                    <Button type="red" onClick={handleClose} disabled={isTransferring}>
                        Cancel
                    </Button>
                    <Button disabled={isInvalidAmount || isTransferring} onClick={handleTransfer}>
                        {isTransferring ? "Transferring..." : "Transfer"}
                    </Button>
                </div>
            </div>
        </Dialog>
    );
};

export default TransferCreditsModal;
